import * as React from 'react'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - No types available for @rails/actioncable
import { createConsumer } from '@rails/actioncable'
import { match } from 'ts-pattern'
import { AppConfig } from 'store/modules/config'
import {
  Analysis,
  ChatSession,
  Message,
  OrgSnapshot,
  WebSocketMessage,
} from './types'
import {
  transformAnalysis,
  transformChat,
  transformMessage,
  transformOrgSnapshot,
} from './transforms'

/* eslint-disable promise/always-return */
/* eslint-disable promise/catch-or-return */
/* eslint-disable promise/prefer-await-to-then */

/**
 * The main logic for Workforce Intelligence.
 * This hook handles hitting the appropriate endpoints,
 * setting up the WebSocket connection with Actioncable,
 * and managing state for the WFI experience.
 *
 * When the user first tries to send a message, we POST to the `chats` endpoint to create a chat.
 * Then we attempt to create a WebSocket connection for that chat in `createChat`.
 * Handling of incoming messages is defined there.
 *
 * `analyses` is the list of analyses for a particular chat.
 * `chats` is the list of chat sessions.
 * `orgSnapshot` is the org snapshot created when the chat is created.
 * `messages` is the user's and the chatbot's messages.
 * `skillRequirements` is the user's current adjustments to desired skills.
 */
export const useWorkforceIntelligence = () => {
  type Chat = {
    id: number | null
    orgSnapshotId: number | null
  }
  const [chats, setChats] = React.useState<ChatSession[]>([])
  const [currentChat, setCurrentChat] = React.useState<Chat>({
    id: null,
    orgSnapshotId: null,
  })
  const [orgSnapshot, setOrgSnapshot] = React.useState<OrgSnapshot | null>(null)
  const [skillRequirements, setSkillRequirements] = React.useState<
    Map<number, number>
  >(new Map())
  const [analyses, setAnalyses] = React.useState<Analysis[]>([])
  const [messages, setMessages] = React.useState<Message[]>([])
  const [messageInProgress, setMessageInProgress] = React.useState<
    string | null
  >(null)
  const [loading, setLoading] = React.useState(false)
  // the Actioncable connection
  const cable = React.useRef(null)
  // the individual subscription for a specific chat
  const subscription = React.useRef(null)

  /**
   * any state that should be cleared when we switch between chats.
   */
  const resetState = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(subscription.current as any)?.unsubscribe()
    subscription.current = null
    setAnalyses([])
    setMessages([])
  }

  // on mount, we fetch the list of previous chats.
  // if the current chat changes, we check to if it's a new chat.
  // if it is, we refetch the list of chats
  React.useEffect(() => {
    if (
      (currentChat.id == null && chats.length !== 0) ||
      chats.some((chat) => chat.id === currentChat.id)
    )
      return

    // Filtered by type: this endpoint returns every chat the user owns, so
    // unfiltered it listed the career coach's threads in this sidebar too, and
    // would now list Ask AI's. Creating one already defaults to WfiChat.
    fetch('/api/v1/internal/chats?chat_type=WfiChat')
      .then(async (res) => {
        if (res.ok) {
          const { data: chats } = await res.json()
          setChats((chats ?? []).map(transformChat))
        } else {
          throw new Error('unable to get list of chats')
        }
      })
      // eslint-disable-next-line promise/prefer-await-to-callbacks
      .catch((err) => console.error(err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat.id])

  // on mount, we create a new org_snapshot.
  // this is the info in the `KvBox`es above the chat,
  // the organization's skills for `SkillsSidebar`,
  // and users for `HeadcountOverview`.
  // we also create a new org_snapshot if the user ever leaves an existing chat
  // to create a new one.
  React.useEffect(() => {
    if (currentChat.orgSnapshotId != null) return

    fetch('/api/v1/org_snapshots', {
      method: 'POST',
    })
      .then(async (res) => {
        if (res.ok) {
          const { data } = await res.json()
          setOrgSnapshot(transformOrgSnapshot(data))
        } else {
          throw new Error('unable to create org snapshot')
        }
      })
      // eslint-disable-next-line promise/prefer-await-to-callbacks
      .catch((err) => console.error(err))
  }, [currentChat.orgSnapshotId])

  const createChat = async () => {
    const url =
      orgSnapshot == null
        ? '/api/v1/internal/chats'
        : `/api/v1/internal/chats?org_snapshot_id=${orgSnapshot.id}`

    const isNewChat = currentChat.id == null
    const chatId =
      currentChat.id ??
      (await fetch(url, {
        method: 'POST',
      }).then(async (res) => {
        if (res.ok) {
          const { data } = await res.json()
          setCurrentChat({
            id: data.id,
            orgSnapshotId: data.attributes.org_snapshot_id,
          })
          return data.id
        } else {
          throw new Error('unable to create new chat')
        }
      }))

    if (isNewChat && skillRequirements.size > 0) {
      fetch(`/api/v1/internal/chats/${chatId}/update_skill_requirements`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          skill_requirements: Array.from(skillRequirements.entries()).map(
            ([id, level]) => ({
              skill_id: id,
              skill_variant_id: id,
              min_level: level,
            }),
          ),
        }),
      }).catch(console.error)
    }

    // we can keep the same WS connection when the user switches chats,
    // we just need to subscribe to a new topic.
    if (cable.current == null) {
      const wsUrl = AppConfig.apiUrl.replace(/^http/, 'ws') + '/cable'
      cable.current = createConsumer(wsUrl)
    }

    return new Promise((resolve) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      subscription.current = (cable.current as any).subscriptions.create(
        {
          channel: 'Api::ChatChannel',
          chat_id: chatId,
        },
        {
          // make sure `createChat` doesn't resolve
          // until the subscription is open and ready for us to send messages.
          connected: () => {
            resolve(null)
          },
          received: (message: WebSocketMessage) => {
            const messageType = message.type
            // main logic for handling WS messages is this match
            match(messageType)
              // the server has acknowledged that the user has sent a message.
              .with('message_received', () => {
                setLoading(true)
              })
              // each chunk has the entire currently available message.
              // essentially, the server appends chunks together for us
              // and we get the whole thing every time.
              .with('response_chunk', () => {
                setLoading(false)
                setMessageInProgress(message?.content || null)
              })
              // we're done streaming the AI response
              // and we can start allowing the user to send a message again.
              .with('response_completed', () => {
                setMessageInProgress(null)
                setMessages((messages) => [
                  ...messages,
                  {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    id: (message.message as any)?.id,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    content: (message.message as any)?.content,
                    role: 'assistant',
                  },
                ])
              })
              // an analysis has been created based on the user's prompt.
              // we need to add it to the list of analyses.
              .with('analysis_created', () => {
                // const associatedMessageId = message.message.id
                fetch(`/api/v1/internal/chats/${chatId}/analyses`)
                  .then(async (res) => {
                    if (res.ok) {
                      const json = await res.json()
                      setAnalyses(json.data.map(transformAnalysis))
                    } else {
                      throw new Error('unable to get analyses')
                    }
                  })
                  // eslint-disable-next-line promise/prefer-await-to-callbacks
                  .catch((err) => console.error(err))
              })
              .with('error', () => {
                // TODO: error state
              })
              .otherwise(() => {
                console.log(`got unknown message type ${messageType}`)
                console.log(message)
              })
          },
        },
      )
    })
  }

  // if the user switches to a different chat,
  // this effect updates the org snapshot to the one associated with that chat.
  React.useEffect(() => {
    if (
      currentChat.orgSnapshotId == null ||
      // no need to update if we already have the same org snapshot
      currentChat.orgSnapshotId === orgSnapshot?.id
    )
      return

    fetch(`/api/v1/org_snapshots/${currentChat.orgSnapshotId}`)
      .then(async (res) => {
        if (res.ok) {
          const { data } = await res.json()
          setOrgSnapshot(transformOrgSnapshot(data))
        } else {
          throw new Error('unable to get org snapshot')
        }
      })
      // eslint-disable-next-line promise/prefer-await-to-callbacks
      .catch((err) => console.error(err))
  }, [orgSnapshot?.id, currentChat.orgSnapshotId])

  const deleteChat = async (id: number) => {
    fetch(`/api/v1/internal/chats/${id}/`, {
      method: 'DELETE',
    })
      .then((res) => {
        if (res.ok) {
          setChats((chats) => chats.filter((chat) => chat.id !== id))
        } else {
          throw new Error('unable to delete chat')
        }
      })
      // eslint-disable-next-line promise/prefer-await-to-callbacks
      .catch((err) => console.error(err))
  }

  const sendMessage = async (content: string) => {
    setLoading(true)
    if (subscription.current == null) {
      try {
        await createChat()
      } catch (err) {
        console.error(err)
        return
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(subscription.current as any).send({ content })
    setMessages((messages) => [
      ...messages,
      { id: 'TODO', content, role: 'user' },
    ])
  }

  const starChat = async (id: number) => {
    fetch(`/api/v1/internal/chats/${id}/?is_starred=true`, {
      method: 'PATCH',
    })
      .then((res) => {
        if (res.ok) {
          setChats((chats) =>
            chats.map((chat) =>
              chat.id === id ? { ...chat, starred: true } : chat,
            ),
          )
        } else {
          throw new Error('unable to star chat')
        }
      })
      // eslint-disable-next-line promise/prefer-await-to-callbacks
      .catch((err) => console.error(err))
  }

  const unstarChat = async (id: number) => {
    fetch(`/api/v1/internal/chats/${id}/?is_starred=false`, {
      method: 'PUT',
    })
      .then((res) => {
        if (res.ok) {
          setChats((chats) =>
            chats.map((chat) =>
              chat.id === id ? { ...chat, starred: false } : chat,
            ),
          )
        } else {
          throw new Error('unable to unstar chat')
        }
      })
      // eslint-disable-next-line promise/prefer-await-to-callbacks
      .catch((err) => console.error(err))
  }

  const switchChat = (chatId: number | null, orgSnapshotId: number | null) => {
    resetState()
    setCurrentChat({
      id: chatId,
      orgSnapshotId,
    })

    if (chatId == null) return

    fetch(`/api/v1/internal/chats/${chatId}/analyses`)
      .then(async (res) => {
        if (res.ok) {
          const json = await res.json()
          setAnalyses(json.data.map(transformAnalysis))
        } else {
          throw new Error('unable to get analyses')
        }
      })
      // eslint-disable-next-line promise/prefer-await-to-callbacks
      .catch((err) => console.error(err))

    fetch(`/api/v1/internal/chats/${chatId}/`)
      .then(async (res) => {
        if (res.ok) {
          const json = await res.json()
          setMessages((json.included ?? []).reverse().map(transformMessage))
          const requirements: { skill_id: number; min_level: number }[] =
            json.data?.attributes?.skill_requirements ?? []
          setSkillRequirements(
            new Map(requirements.map((r) => [r.skill_id, r.min_level])),
          )
        } else {
          throw new Error('unable to get messages')
        }
      })
      // eslint-disable-next-line promise/prefer-await-to-callbacks
      .catch((err) => console.error(err))
  }

  const reorderAnalyses = (fromIndex: number, toIndex: number) => {
    setAnalyses((analyses) => {
      const result = [...analyses]
      const [removed] = result.splice(fromIndex, 1)
      result.splice(toIndex, 0, removed)

      const analysisIds = result.map((a) => a.id)
      fetch(`/api/v1/internal/chats/${currentChat.id}/analyses_order`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ analysis_ids: analysisIds }),
        // eslint-disable-next-line promise/prefer-await-to-callbacks
      }).catch((err) => console.error(err))

      return result
    })
  }

  return {
    analyses,
    chats,
    currentChatId: currentChat.id,
    deleteChat,
    loading,
    messages:
      messageInProgress == null
        ? messages
        : [
            ...messages,
            {
              role: 'assistant' as const,
              content: messageInProgress,
            },
          ],
    orgSnapshot,
    reorderAnalyses,
    sendMessage,
    skillRequirements,
    starChat,
    unstarChat,
    switchChat,
    updateSkillRequirements: (changedSkills: Map<number, number>) => {
      setSkillRequirements(changedSkills)

      if (currentChat.id == null) return

      fetch(
        `/api/v1/internal/chats/${currentChat.id}/update_skill_requirements`,
        {
          headers: {
            'content-type': 'application/json',
          },
          method: 'PUT',
          body: JSON.stringify({
            skill_requirements: Array.from(changedSkills.entries()).map(
              ([id, level]) => ({
                skill_id: id,
                // TODO: this is NOT right, but should work for the demo
                skill_variant_id: id,
                min_level: level,
              }),
            ),
          }),
        },
      ).catch(console.error)
    },
  }
}
